import React, { useCallback } from "react";
import { MapPinIcon } from "@heroicons/react/24/outline";
import { cx } from "../../utils/cx";
import { useDistance } from "../../hooks/useDistance";
import { useTimeAgo } from "../../hooks/useTimeAgo";
import { useSettings } from "../../contexts/SettingsContext";
import { extractCityFromAddressString } from "../../utils/geocoding";
import { Identity } from "./Identity";
import { ActionSentence } from "./ActionSentence";
import { SecondaryContent } from "./SecondaryContent";

export function buildAriaLabel(n, timeAgo) {
  const kind =
    n.type === "like"
      ? "liked your note"
      : n.type === "comment"
      ? "commented on your note"
      : n.type === "comment_like"
      ? "liked your comment on"
      : n.type === "nearby_note" && n.noteId
      ? "left a trace near you"
      : "new note nearby";

  const who =
    n.type === "nearby_note" && !n.noteId
      ? undefined
      : Array.isArray(n.actors) && n.actors.length > 1
      ? n.actors
          .map((a) => a.name)
          .filter(Boolean)
          .slice(0, 2)
          .join(", ") +
        (n.actors.length > 2 ? ` and ${n.actors.length - 2} others` : "")
      : n.actorName;

  /**
   * Build a string.
   *  Remove falsy values (like undefined) before joining string
   */
  return [
    who,
    kind,
    n.noteTitle,
    n.placeName ? `near ${n.placeName}` : undefined,
    `— ${timeAgo}`,
    n.read ? "" : "(unread)",
  ]
    .filter(Boolean)
    .join(" ");
}

export function NotificationCard({
  notification,
  onOpenNote,
  onMarkRead,
  userCoords = { lng: -121.942684819, lat: 37.502603089 },
}) {
  const {
    id,
    type,
    actorName,
    avatarUrl = "",
    actors = [],
    noteId,
    noteTitle,
    placeName,
    commentSnippet,
    message,
    createdAt,
    read,
    coords,
  } = notification;

  // Get user's unit preference from settings
  const { settings } = useSettings();
  const unit = settings?.units || "miles";
  const unitSuffix = unit === "miles" ? "mi" : "km";

  // Auto-updating time display
  const { display: timeAgo, fullDateTime } = useTimeAgo(createdAt);

  const open = useCallback(() => {
    onMarkRead?.(id);
    onOpenNote?.(id);
  }, [id, onMarkRead, onOpenNote]);

  /**
   * Used to calculate the distance from the user and the note on which the notification was left
   */
  const distance = useDistance(userCoords || null, coords || null, unit);

  return (
    <li className="relative">
      <div
        role="button"
        tabIndex={0}
        aria-label={buildAriaLabel(notification, timeAgo)}
        onClick={open}
        onKeyDown={(e) => (e.key === "Enter" ? open() : null)}
        className={cx(
          "group relative flex items-start gap-3 rounded-2xl border px-4 pt-6 pb-4 shadow-sm transition hover:shadow",
          read
            ? "bg-surface border-border-light"
            : "bg-surface-elevated border-border-strong"
        )}
      >
        {/* Top-right meta row */}
        <div className="absolute top-2 right-3 flex items-center gap-1.5">
          {!read && (
            <span
              className="inline-block h-2 w-2 rounded-full bg-primary ring-2 ring-border-light"
              aria-hidden="true"
            />
          )}
          <span className="text-xs text-text-muted whitespace-nowrap select-none" title={fullDateTime}>
            {timeAgo}
          </span>
        </div>

        {/* Identity */}
        <Identity
          type={type}
          read={read}
          actorName={actorName}
          avatarUrl={avatarUrl}
          actors={actors}
          noteId={noteId}
        />

        {/* Content */}
        <div className="min-w-0 flex-1">
          <ActionSentence
            type={type}
            actorName={actorName}
            noteId={noteId}
            noteTitle={noteTitle}
            message={message}
            read={read}
            actors={actors}
          />
          <SecondaryContent
            type={type}
            noteId={noteId}
            noteTitle={noteTitle}
            commentSnippet={commentSnippet}
            message={message}
          />

          {/* Place chip (with optional distance) */}
          {placeName && (
            <div
              className={cx(
                "mt-1 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border",
                read
                  ? "bg-transparent text-text-secondary border-border-strong"
                  : "bg-transparent text-primary border-primary"
              )}
            >
              <MapPinIcon className="h-3.5 w-3.5" />
              <span className="line-clamp-1">
                {extractCityFromAddressString(placeName)}
                {distance != null ? (
                  <>
                    {" "}
                    · {distance} {unitSuffix}
                  </>
                ) : null}
              </span>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
